import React, { useState, useEffect, useRef } from "react";
import Navbar from "./NavBar";
import "../styles/Home.css";
import Footer from "./Footer";
import Seo from "./Seo";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import {
  cmsHeroBackgroundStyle,
  CmsImage,
  resolveFirestoreImage,
  useCmsHeroImageReady,
} from "../firestoreMedia";

const GALLERY_DEFAULTS = [
  { src: "./images/ranch.png", label: "Ranch" },
  { src: "./images/cottage.png", label: "Cottage" },
  { src: "./images/clubhouse.png", label: "Clubhouse" },
  { src: "./images/sweetshoppe.png", label: "Sweet Shoppe" },
];

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
  authDomain: "wishuponaplayhouse.firebaseapp.com",
  projectId: "wishuponaplayhouse",
  storageBucket: "wishuponaplayhouse.firebasestorage.app",
  messagingSenderId: "513377368862",
  appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
  measurementId: "G-NZ116LS0Q1",
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

function Home() {
  const [paragraph, setParagraph] = useState("");
  const [heroImage, setHeroImage] = useState(null);
  const [heroEyebrow, setHeroEyebrow] = useState("Denver, Colorado");
  const [heroTagline, setHeroTagline] = useState(
    "Make Your Child's Wish Come True",
  );
  const [aboutTitle, setAboutTitle] = useState(
    "Built for play. Made for Magic.",
  );
  const [aboutImage, setAboutImage] = useState(null);
  const [stripImage, setStripImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState(null);
  const [ctaBannerText, setCtaBannerText] = useState("Start My Design");
  const [dataLoaded, setDataLoaded] = useState(false);

  const bgRef = useRef(null);

  // ── Parallax: move bg image at 35% of scroll speed ──
  useEffect(() => {
    let ticking = false;
    const bg = bgRef.current;

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (bg) {
            const y = window.pageYOffset * 0.38;
            bg.style.transform = `translateY(${y}px)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Intersection Observer: reveal sections on scroll ──
  useEffect(() => {
    if (!dataLoaded) return;
    const els = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [dataLoaded]);

  // ── Firebase ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(firestore, "Pages", "HomePage");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setParagraph(data.paragraph || "");
          if (data.heroImage) setHeroImage(data.heroImage);
          if (data.heroEyebrow) setHeroEyebrow(data.heroEyebrow);
          if (data.heroTagline) setHeroTagline(data.heroTagline);
          if (data.aboutTitle) setAboutTitle(data.aboutTitle);
          if (data.aboutImage) setAboutImage(data.aboutImage);
          if (data.stripImage) setStripImage(data.stripImage);
          if (data.galleryImages) setGalleryImages(data.galleryImages);
          if (data.ctaBannerText) setCtaBannerText(data.ctaBannerText);
        }
      } catch (error) {
        console.error("Error fetching homepage content: ", error);
      } finally {
        setDataLoaded(true);
      }
    };
    fetchData();
  }, []);

  const homeHeroUrl = dataLoaded
    ? resolveFirestoreImage(heroImage, "/images/hero.png")
    : "";
  const homeHeroReady = useCmsHeroImageReady(homeHeroUrl, dataLoaded);

  return (
    <div>
      <Seo
        title="Custom Playhouses in Denver, Colorado | Wish Upon A Playhouse"
        description="Handcrafted custom playhouses built on-site in Denver, Colorado. Ranch, Cottage, Clubhouse, and Sweet Shoppe designs tailored to your backyard. Serving Colorado families."
        path="/"
      />
      <Navbar />
      <>
        {/* ── HERO ── */}
        <section className="home-hero">
          {/* Parallax bg — moves slower than scroll via JS */}
          <div
            className={`hero-bg${dataLoaded ? "" : " hero-bg--pending"}`}
            ref={bgRef}
            style={{
              ...cmsHeroBackgroundStyle(
                !dataLoaded,
                heroImage,
                "/images/hero.png",
              ),
              opacity: dataLoaded ? (homeHeroReady ? 1 : 0) : 1,
              transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
          <div className="hero-overlay" />
          <div
            className={`hero-content${dataLoaded ? " hero-content--loaded" : ""}`}
          >
            <p className="hero-eyebrow">{heroEyebrow}</p>
            <h1 className="hero-logo-text">
              Wish Upon a <span className="hero-logo-text__line">Playhouse</span>
              <span className="sr-only"> — Custom Playhouses in Denver, Colorado</span>
            </h1>
            <div className="hero-rule" />
            <p className="hero-tagline">{heroTagline}</p>
            <a href="/gallery" className="hero-cta">
              <span>View Gallery</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 8H13M13 8L9 4M13 8L9 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
          {/* Scroll cue */}
          <div
            className={`hero-scroll-cue${dataLoaded ? " hero-content--loaded" : ""}`}
          >
            <div className="scroll-line" />
          </div>
        </section>

        {/* ── SCROLL CONTENT — white bg slides over hero ── */}
        <div className="home-scroll-content">
          {/* About */}
          <div className="home-about reveal">
            <CmsImage
              pending={!dataLoaded}
              image={aboutImage}
              fallback="./images/mainstory.webp"
              alt="Wish Upon A Playhouse — Denver, Colorado custom children's playhouse builders"
              className="about-img"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "./images/ranch.png";
              }}
            />
            <div className="about-text-col">
              <h2 className="about-section-title">{aboutTitle}</h2>
              <p className="about-section-body">{paragraph}</p>
              <a href="/about" className="about-learn-btn">
                <span>Learn More</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 8H13M13 8L9 4M13 8L9 12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>

          {/* Panoramic strip */}
          <div className="home-strip reveal">
            <CmsImage
              pending={!dataLoaded}
              image={stripImage}
              fallback="./images/sub-hero.png"
              alt="Handcrafted custom children's playhouse built on-site in Denver, Colorado"
            />
          </div>

          {/* Gallery preview */}
          <div className="home-gallery-section">
            <div className="section-header-wtp reveal">
              <p className="gallery-eyebrow">Our Work</p>
              <h2>Project Gallery</h2>
            </div>
            <div className="gallery-preview-grid">
              {(galleryImages || GALLERY_DEFAULTS).map((item, i) => (
                <a
                  href="/gallery"
                  key={item.label || i}
                  className="gallery-preview-item reveal"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <CmsImage
                    pending={!dataLoaded}
                    image={item}
                    fallback={GALLERY_DEFAULTS[i]?.src || ""}
                    alt={`${item.label} playhouse — custom-built in Denver, Colorado by Wish Upon A Playhouse`}
                  />
                  <div className="gallery-item-label">
                    <span>{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
            <div className="gallery-show-more reveal">
              <a href="/gallery" className="gallery-show-btn">
                Show More
              </a>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="home-cta-banner reveal">
            <a href="/contact" className="cta-banner-inner">
              {["#3d342a", "#5D5548", "#7C7367", "#8A8175", "#7C7367", "#5D5548", "#3d342a"].map(
                (c, i) => (
                  <div
                    key={i}
                    className="cta-swatch"
                    style={{ background: c }}
                  />
                ),
              )}
              <div className="cta-banner-text-wrap">
                <span className="cta-banner-text">{ctaBannerText}</span>
              </div>
            </a>
          </div>
        </div>
      </>
      <Footer />
    </div>
  );
}

export default Home;
